import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "renova360_cookie_consent_v1";

function getStoredConsent() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function persistConsent(value) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            ...value,
            timestamp: new Date().toISOString(),
        }));
    } catch { /* storage disabled, ignore */ }
}

export default function CookieBanner() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [showCustom, setShowCustom] = useState(false);
    const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

    useEffect(() => {
        if (!getStoredConsent()) {
            setOpen(true);
        }
    }, []);

    const acceptAll = () => {
        persistConsent({ essential: true, analytics: true, marketing: true });
        setOpen(false);
    };

    const onlyEssential = () => {
        persistConsent({ essential: true, analytics: false, marketing: false });
        setOpen(false);
    };

    const savePrefs = () => {
        persistConsent({ essential: true, ...prefs });
        setOpen(false);
    };

    if (!open) return null;

    return (
        <div
            className="fixed bottom-0 inset-x-0 z-[80] px-4 md:px-8 pb-4 md:pb-6 pointer-events-none animate-fade-up"
            data-testid="cookie-banner"
        >
            <div className="max-w-5xl mx-auto bg-[#0a0a0c]/95 backdrop-blur-xl border border-gold/30 rounded-sm shadow-[0_8px_40px_rgba(0,0,0,0.6)] pointer-events-auto p-5 md:p-6">
                <div className="flex items-start gap-4">
                    <div className="hidden md:flex items-center justify-center w-11 h-11 rounded-sm bg-gold/15 shrink-0">
                        <Cookie size={20} className="text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-white font-semibold text-sm tracking-wide" data-testid="cookie-title">{t("cookie.title")}</h3>
                        <p className="text-gray-400 text-xs leading-relaxed mt-1.5">
                            {t("cookie.body_1")}<Link to="/cookie-policy" className="text-gold underline hover:text-gold-light">{t("cookie.body_link_cookies")}</Link>{t("cookie.body_and_in")}<Link to="/privacy" className="text-gold underline hover:text-gold-light">{t("cookie.body_link_privacy")}</Link>.
                        </p>

                        {showCustom && (
                            <div className="mt-4 grid sm:grid-cols-3 gap-3 text-xs" data-testid="cookie-prefs">
                                <PrefRow
                                    label={t("cookie.essential_label")} desc={t("cookie.essential_desc")}
                                    checked disabled
                                />
                                <PrefRow
                                    label={t("cookie.analytics_label")} desc={t("cookie.analytics_desc")}
                                    checked={prefs.analytics}
                                    onChange={(v) => setPrefs({ ...prefs, analytics: v })}
                                    testid="pref-analytics"
                                />
                                <PrefRow
                                    label={t("cookie.marketing_label")} desc={t("cookie.marketing_desc")}
                                    checked={prefs.marketing}
                                    onChange={(v) => setPrefs({ ...prefs, marketing: v })}
                                    testid="pref-marketing"
                                />
                            </div>
                        )}

                        <div className="mt-4 flex flex-wrap gap-2">
                            {!showCustom ? (
                                <>
                                    <button onClick={acceptAll}
                                        className="bg-gold text-black font-semibold px-5 py-2 rounded-sm text-xs tracking-widest uppercase hover:bg-gold-light transition-colors"
                                        data-testid="cookie-accept-all">
                                        {t("cookie.accept_all")}
                                    </button>
                                    <button onClick={onlyEssential}
                                        className="border border-white/20 text-white px-5 py-2 rounded-sm text-xs tracking-widest uppercase hover:border-gold hover:text-gold transition-colors"
                                        data-testid="cookie-only-essential">
                                        {t("cookie.only_essential")}
                                    </button>
                                    <button onClick={() => setShowCustom(true)}
                                        className="text-gray-400 hover:text-white px-3 py-2 text-xs tracking-widest uppercase"
                                        data-testid="cookie-customize">
                                        {t("cookie.customize")}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button onClick={savePrefs}
                                        className="bg-gold text-black font-semibold px-5 py-2 rounded-sm text-xs tracking-widest uppercase hover:bg-gold-light transition-colors"
                                        data-testid="cookie-save-prefs">
                                        {t("cookie.save_prefs")}
                                    </button>
                                    <button onClick={() => setShowCustom(false)}
                                        className="text-gray-400 hover:text-white px-3 py-2 text-xs tracking-widest uppercase">
                                        {t("cookie.back")}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onlyEssential}
                        className="text-gray-500 hover:text-white shrink-0"
                        aria-label={t("cookie.close_aria")}
                        data-testid="cookie-close"
                    ><X size={18} /></button>
                </div>
            </div>
        </div>
    );
}

function PrefRow({ label, desc, checked, disabled, onChange, testid }) {
    return (
        <label className={`flex items-start gap-2 border border-white/10 rounded-sm p-3 ${disabled ? "opacity-60" : "cursor-pointer hover:border-gold/40"}`}>
            <input
                type="checkbox" checked={checked} disabled={disabled}
                onChange={(e) => onChange?.(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-gold"
                data-testid={testid}
            />
            <div>
                <div className="text-white font-semibold text-[11px] tracking-wide uppercase">{label}</div>
                <div className="text-gray-500 text-[10px] mt-0.5">{desc}</div>
            </div>
        </label>
    );
}
